import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Pagination from '../../../components/Pagination';
import { rembursment, status } from '../../../utils/Helpers';
import moment from "moment";
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { ClipboardList, Info, Activity, Clock, FileText } from "lucide-react";
import _ from "lodash";

const RembursmentList = ({ Reimbursements, itemsPerPage, setOffset }) => {
    const userProfile = useSelector((state) => state.user);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedContent, setSelectedContent] = useState('');
    const [isCommentPopup, setIsCommentPopup] = useState(false);
    const [selectedComment, setSelectedComment] = useState('');

    const handleOpenPopup = (content) => {
        setSelectedContent(content);
        setIsPopupOpen(true);
    };

    const toggleCommentPopup = (comment = '') => {
        setSelectedComment(comment);
        setIsCommentPopup(!isCommentPopup);
    };

    const formatCreatedAt = (date) => moment(date).format("DD MMM YYYY [AT] h:mm A");

    const statusConfig = {
        1: { label: "Pending", color: "amber" },
        2: { label: "Rejected", color: "red" },
        3: { label: "Approved", color: "green" },
    };

    const colorMap = {
        amber: {
            badge: "bg-amber-500/10 text-amber-500 border-amber-500/40",
            dot: "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]",
        },
        green: {
            badge: "bg-green-500/10 text-green-500 border-green-500/40",
            dot: "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]",
        },
        red: {
            badge: "bg-red-500/10 text-red-500 border-red-500/40",
            dot: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]",
        },
    };

    const hasRequests = !_.isEmpty(Reimbursements) && Reimbursements.length > 0;

    return (
        <>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                className="space-y-3 mt-6 w-full max-w-7xl mx-auto hidden xl:block px-4"
            >
                {/* Header row */}
                <div className="flex items-center justify-between mb-1 px-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <Activity className="w-5 h-5 text-blue-500" />
                        Reimbursement Requests
                    </h3>
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Total: {Reimbursements?.length || 0}
                    </span>
                </div>

                {/* Desktop List — Card per row */}
                <div className="hidden md:block bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-blue-gray-900 shadow-sm">
                    {/* Column Headers */}
                    <div className="grid grid-cols-[1.2fr_1fr_1fr_1.4fr_1fr_1.2fr_auto] gap-4 px-6 py-3 border-b border-gray-100 dark:border-gray-800/60 bg-gray-50/60 dark:bg-gray-800/40">
                        <div className="text-xs font-semibold text-gray-400 ml-3 uppercase tracking-widest">
                            Request Type
                        </div>
                        <div className="text-xs font-semibold text-gray-400 ml-3 uppercase tracking-widest">
                            Amount
                        </div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                            Date
                        </div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                            Detail
                        </div>
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                            PDF
                        </div>
                        <div className="text-xs font-semibold text-gray-400 -ml-3 uppercase tracking-widest">
                            Created At
                        </div>
                        <div className="text-xs font-semibold text-gray-400 me-10 uppercase tracking-widest">
                            Status
                        </div>
                    </div>

                    {/* Rows */}
                    <div className="p-3 space-y-2">
                        {hasRequests ? (
                            <AnimatePresence mode="popLayout">
                                {Reimbursements.map((request, index) => {
                                    const cfg = statusConfig[request.RequestStatus];
                                    const colors = cfg ? colorMap[cfg.color] : colorMap.amber;

                                    return (
                                        <motion.div
                                            key={request.id || index}
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0, transition: { delay: index * 0.03 } }}
                                            exit={{ opacity: 0, scale: 0.97 }}
                                            className="grid grid-cols-[1.2fr_1fr_1fr_1.4fr_1fr_1.2fr_auto] gap-4 items-center px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 hover:bg-blue-50/30 dark:hover:bg-blue-900/10 rounded-xl border border-gray-100 dark:border-gray-800/40 transition-colors"
                                        >
                                            {/* Request Type */}
                                            <div className="flex items-center gap-2 min-w-0">
                                                <div className="p-1.5 bg-blue-50 dark:bg-blue-500/10 rounded-lg flex-shrink-0">
                                                    <ClipboardList className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div className="text-[14px] font-light text-gray-700 dark:text-gray-300 truncate">
                                                    {rembursment[request.RequestType]}
                                                </div>
                                            </div>

                                            {/* Amount */}
                                            <div className="text-[14px] font-light text-gray-900 dark:text-gray-100 uppercase tracking-tight">
                                                Rs {request.InvoiceAmount}
                                            </div>

                                            {/* Date */}
                                            <div className="text-[14px] font-light text-gray-600 dark:text-gray-400 uppercase tracking-tight">
                                                {moment(request.Date).format("DD MMM YYYY")}
                                            </div>

                                            {/* Detail — always show View Details */}
                                            <div className="min-w-0">
                                                {request.RequestDetail ? (
                                                    <button
                                                        onClick={() => handleOpenPopup(request.RequestDetail)}
                                                        className="text-[13px] font-light text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                                    >
                                                        View Details 
                                                    </button>
                                                ) : (
                                                    <span className="text-[13px] font-light text-gray-400">—</span>
                                                )}
                                            </div>

                                            {/* PDF */}
                                            <div className="min-w-0 flex items-center gap-1.5">
                                                {request.InvoicePath ? (
                                                    <a
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        href={`https://portalbackend.gentrickstaffing.com${request.InvoicePath}`}
                                                    className="flex items-center gap-1.5 text-[13px] font-light text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                                    >
                                                        <FileText className="w-3.5 h-3.5" />
                                                        View PDF
                                                    </a>
                                                ) : (
                                                    <span className="text-[13px] font-light text-gray-400">—</span>
                                                )}
                                            </div>

                                            {/* Created At */}
                                            <div className="text-[12px] font-light text-gray-400 dark:text-gray-500 uppercase tracking-tight leading-relaxed">
                                                {formatCreatedAt(request.CreatedAt)}
                                            </div>

                                            {/* Status + Comment Icon */}
                                            <div className="flex items-center justify-end gap-1">
                                                {request.RequestStatus === 2 && request.Comment && (
                                                    <button
                                                        onClick={() => toggleCommentPopup(request.Comment)}
                                                        className="p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                        title="View Comment"
                                                    >
                                                        <Info className="w-3 h-3 text-blue-500" />
                                                    </button>
                                                )}
                                                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-[11px] font-bold border uppercase tracking-[0.08em] whitespace-nowrap ${colors.badge}`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full me-1.5 ${colors.dot}`} />
                                                    {cfg?.label ?? "Pending"}
                                                </span>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        ) : (
                            <div className="py-20 flex flex-col items-center justify-center">
                                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                                    <Info className="w-8 h-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 font-medium text-base">
                                    No Reimbursement found.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Cards */}
                <div className="flex flex-col gap-3 md:hidden">
                    {hasRequests ? (
                        <AnimatePresence mode="popLayout">
                            {Reimbursements.map((request, index) => {
                                const cfg = statusConfig[request.RequestStatus];
                                const colors = cfg ? colorMap[cfg.color] : colorMap.amber;

                                return (
                                    <motion.div
                                        key={request.id || index}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        className="bg-white dark:bg-gray-900/40 border border-gray-100 dark:border-blue-gray-900 rounded-2xl p-5 shadow-sm space-y-4"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-lg">
                                                    <ClipboardList className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                                    {rembursment[request.RequestType]}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {request.RequestStatus === 2 && request.Comment && (
                                                    <button
                                                        onClick={() => toggleCommentPopup(request.Comment)}
                                                        className="p-0.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                        title="View Comment"
                                                    >
                                                        <Info className="w-3 h-3 text-blue-500" />
                                                    </button>
                                                )}
                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold border uppercase ${colors.badge}`}>
                                                    <span className={`w-1 h-1 rounded-full me-1.5 ${colors.dot}`} />
                                                    {cfg?.label ?? "Pending"}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3 py-3 border-y border-gray-50 dark:border-gray-800/50">
                                            <div>
                                                <div className="text-[9px] text-gray-400 uppercase font-black tracking-widest mb-1">Date</div>
                                                <div className="text-[11px] font-medium text-gray-700 dark:text-gray-300 uppercase">
                                                    {moment(request.Date).format("DD MMM YYYY")}
                                                </div>
                                            </div>
                                            <div>
                                                <div className="text-[9px] text-gray-400 uppercase font-black tracking-widest mb-1">Amount</div>
                                                <div className="text-[11px] font-bold text-gray-900 dark:text-white uppercase">
                                                    Rs {request.InvoiceAmount}
                                                </div>
                                            </div>
                                        </div>

                                        {request.RequestDetail && (
                                            <div className="pb-3 border-b border-gray-50 dark:border-gray-800/50">
                                                <button
                                                    onClick={() => handleOpenPopup(request.RequestDetail)}
                                                    className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors"
                                                >
                                                    View Details →
                                                </button>
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between pt-1">
                                            <div className="text-[10px] text-gray-400 uppercase">
                                                <Clock className="w-3 h-3 inline me-1" />
                                                {formatCreatedAt(request.CreatedAt)}
                                            </div>
                                            {request.InvoicePath && (
                                                <a
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={`https://portalbackend.gentrickstaffing.com${request.InvoicePath}`}
                                                    className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                                                >
                                                    <FileText className="w-3.5 h-3.5" />
                                                    View PDF
                                                </a>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    ) : (
                        <div className="bg-white dark:bg-gray-900/40 rounded-2xl border border-gray-100 dark:border-blue-gray-900 p-10 text-center">
                            <Info className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                            <p className="text-gray-500 dark:text-gray-400 font-medium">No Reimbursement found.</p>
                        </div>
                    )}
                </div>

                {hasRequests && (
                    <div className="mt-6 flex justify-center w-full">
                        <Pagination
                            totalCount={Reimbursements.length}
                            itemsPerPage={itemsPerPage}
                            onPageChange={setOffset}
                        />
                    </div>
                )}
            </motion.div>

            {/* Detail Popup */}
            {isPopupOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] px-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800"
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                            <h3 className="font-bold text-gray-900 dark:text-white">Request Context</h3>
                            <button
                                onClick={() => setIsPopupOpen(false)}
                                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                                {selectedContent}
                            </p>
                        </div>
                    </motion.div>
                </div>
            )}

            {/* Comment Popup */}
            {isCommentPopup && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[110] px-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 10 }}
                        className="bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800"
                    >
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
                            <h3 className="font-bold text-gray-900 dark:text-white">Comment Details</h3>
                            <button
                                onClick={() => setIsCommentPopup(false)}
                                className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-6 max-h-[60vh] overflow-y-auto custom-scrollbar">
                            <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                                {selectedComment}
                            </p>
                        </div>
                    </motion.div>
                </div>
            )}
        </>
    );
};

export default RembursmentList;